<template>
    <div class="fresh-water-container" v-loading="detailLoading">
        <el-form ref="freshWaterForm" :model="form" label-position="top" size="small">
            <!-- SUPPLIER & VESSEL DETAILS -->
            <div class="section">
                <div class="section-title">SUPPLIER &amp; VESSEL DETAILS</div>

                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="Supplier Name" prop="supplierName">
                            <el-input v-model="form.supplierName" :disabled="!canEditMainForm" />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Account No." prop="accountNo">
                            <el-input v-model="form.accountNo" :disabled="!canEditMainForm" />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Vessel Name" prop="vesselName">
                            <el-input v-model="form.vesselName" :disabled="!canEditMainForm" />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="IMO No." prop="imo">
                            <el-input v-model="form.imo" :disabled="!canEditMainForm" />
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>

            <!-- SUPPLY DETAILS -->
            <div class="section">
                <div class="section-title">SUPPLY DETAILS</div>

                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="Meter Number" prop="meterNumber">
                            <el-input v-model="form.meterNumber" disabled />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item prop="serialNo">
                            <template slot="label">
                                <span>Serial No.</span>
                                <span class="utms-order-no-label">(UTMS Order No.)</span>
                            </template>

                            <el-input v-model="form.serialNo" disabled />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Location of Vessel" prop="locationOfVessel">
                            <el-input v-model="form.locationOfVessel" disabled />
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>

            <!-- BEFORE READINGS -->
            <div class="section">
                <div class="section-title">BEFORE READINGS</div>

                <el-row :gutter="20">
                    <el-col :span="24">
                        <el-form-item label="Supply Start Date and Time" prop="supplyStartDateTime">
                            <el-date-picker v-model="form.supplyStartDateTime" type="datetime"
                                placeholder="dd/mm/yyyy --:--:--" value-format="yyyy-MM-dd HH:mm:ss"
                                format="yyyy-MM-dd HH:mm:ss" style="width: 100%;" :disabled="!canEditBeforeReadings"
                                @change="onSupplyStartChange" />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Reading Before Supply" prop="readingBeforeSupply">
                            <el-input v-model="form.readingBeforeSupply" :disabled="!canEditBeforeReadings">
                            </el-input>
                        </el-form-item>
                    </el-col>

                    <el-col :span="24">
                        <el-form-item label="Before Reading Picture">
                            <div v-if="canEditBeforeReadings" class="image-save-reminder">
                                <i class="el-icon-warning-outline"></i>
                                Please save after uploading the image.
                            </div>
                            <div class="totaliser-image-row">
                                <div v-if="!getReadingPictureUrl(1)">No Image</div>

                                <div v-else>
                                    <el-image style="width: 170px; height: 170px;" :src="getReadingPictureUrl(1)"
                                        :zoom-rate="1.2" :max-scale="7" :min-scale="0.2" fit="fill" />

                                    <input type="file" ref="beforeReadingImage" accept="image/*" :multiple="false"
                                        :disabled="!canEditBeforeReadings"
                                        @change="(event) => handleReadingFileChange(1, event)"
                                        style="opacity: 0; position: fixed; z-index: -1" />

                                    <div class="mt-2">
                                        <el-button size="mini" plain @click="() => handlePreviewReadingImage(1)">
                                            <svg-icon :icon-class="'zoom-in'" />
                                        </el-button>

                                        <el-button v-if="canEditBeforeReadings" size="mini" plain
                                            @click="() => handleOpenReadingImageWindow('before')">
                                            <svg-icon :icon-class="'upload'" />
                                        </el-button>

                                        <el-button v-if="canEditBeforeReadings" size="mini" plain
                                            @click="() => handlePictureRemove(1)">
                                            <svg-icon :icon-class="'delete'" />
                                        </el-button>
                                    </div>
                                </div>

                                <div v-if="!getReadingPictureUrl(1) && canEditBeforeReadings">
                                    <input type="file" ref="beforeReadingImage" accept="image/*" :multiple="false"
                                        :disabled="!canEditBeforeReadings"
                                        @change="(event) => handleReadingFileChange(1, event)"
                                        style="opacity: 0; position: fixed; z-index: -1" />

                                    <el-button size="mini" plain @click="() => handleOpenReadingImageWindow('before')">
                                        <svg-icon :icon-class="'upload'" />
                                    </el-button>
                                </div>
                            </div>
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>

            <!-- AFTER READINGS -->
            <div class="section">
                <div class="section-title">AFTER READINGS</div>

                <el-row :gutter="20">
                    <el-col :span="24">
                        <el-form-item label="Supply End Date and Time" prop="supplyEndDateTime">
                            <el-date-picker v-model="form.supplyEndDateTime" type="datetime"
                                placeholder="dd/mm/yyyy --:--:--" value-format="yyyy-MM-dd HH:mm:ss"
                                format="yyyy-MM-dd HH:mm:ss" style="width: 100%;" :disabled="!canEditAfterReadings"
                                @change="onSupplyEndChange" />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Reading After Supply" prop="readingAfterSupply">
                            <el-input v-model="form.readingAfterSupply" :disabled="!canEditAfterReadings">
                            </el-input>
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <el-form-item label="Quantity Supplied (KL)" prop="quantity">
                            <el-input v-model="form.quantity" disabled>
                                <template slot="append">KL</template>
                            </el-input>
                        </el-form-item>
                    </el-col>

                    <el-col :span="24">
                        <el-form-item label="After Reading Picture">
                            <div v-if="canEditAfterReadings" class="image-save-reminder">
                                <i class="el-icon-warning-outline"></i>
                                Please click Save after uploading the image.
                            </div>
                            <div class="totaliser-image-row">
                                <div v-if="!getReadingPictureUrl(2)">No Image</div>

                                <div v-else>
                                    <el-image style="width: 170px; height: 170px;" :src="getReadingPictureUrl(2)"
                                        :zoom-rate="1.2" :max-scale="7" :min-scale="0.2" fit="fill" />

                                    <input type="file" ref="afterReadingImage" accept="image/*" :multiple="false"
                                        :disabled="!canEditAfterReadings"
                                        @change="(event) => handleReadingFileChange(2, event)"
                                        style="opacity: 0; position: fixed; z-index: -1" />

                                    <div class="mt-2">
                                        <el-button size="mini" plain @click="() => handlePreviewReadingImage(2)">
                                            <svg-icon :icon-class="'zoom-in'" />
                                        </el-button>

                                        <el-button v-if="canEditAfterReadings" size="mini" plain
                                            @click="() => handleOpenReadingImageWindow('after')">
                                            <svg-icon :icon-class="'upload'" />
                                        </el-button>

                                        <el-button v-if="canEditAfterReadings" size="mini" plain
                                            @click="() => handlePictureRemove(2)">
                                            <svg-icon :icon-class="'delete'" />
                                        </el-button>
                                    </div>
                                </div>

                                <div v-if="!getReadingPictureUrl(2) && canEditAfterReadings">
                                    <input type="file" ref="afterReadingImage" accept="image/*" :multiple="false"
                                        :disabled="!canEditAfterReadings"
                                        @change="(event) => handleReadingFileChange(2, event)"
                                        style="opacity: 0; position: fixed; z-index: -1" />

                                    <el-button size="mini" plain @click="() => handleOpenReadingImageWindow('after')">
                                        <svg-icon :icon-class="'upload'" />
                                    </el-button>
                                </div>
                            </div>
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>

            <!-- BILLING DETAILS -->
            <div class="section">
                <div class="section-title">BILLING DETAILS</div>

                <el-row :gutter="20">
                    <el-col :span="24">
                        <el-form-item label="Billing Party Name" prop="billingPartyName">
                            <el-input v-model="form.billingPartyName" disabled />
                        </el-form-item>
                    </el-col>

                    <el-col :span="24">
                        <el-form-item label="Billing Party Code" prop="billingPartyCode">
                            <el-input v-model="form.billingPartyCode" disabled />
                        </el-form-item>
                    </el-col>
                </el-row>
            </div>

            <!-- REMARKS -->
            <div class="section">
                <div class="section-title">REMARKS</div>

                <el-form-item prop="remarks">
                    <el-input v-model="form.remarks" type="textarea" :rows="5" :disabled="!canEditRemarks" />
                </el-form-item>
            </div>

            <!-- DECLARATION -->
            <div class="section">
                <div class="section-title">DECLARATION</div>

                <div class="declaration-text">
                    <p>
                        I hereby declare that the water supplied to the above-mentioned vessel is solely for
                        the vessel's own consumption.
                    </p>
                    <p>
                        I confirm that I have examined and recorded the water meter readings both prior to and
                        after the supply of water. I have also duly notified JPUT of the times at which the
                        vessel commenced and completed the taking of water.
                    </p>
                    <p>
                        I confirm that the quantity of water received on board the vessel, as stated in this
                        Water Service Voucher is accurate and correct.
                    </p>
                    <p>
                        I warrant that I am duly authorised to sign this Water Service Voucher on behalf of the
                        vessel, and I agree that no claim shall be made against JPUT in connection with the
                        supply of water.
                    </p>
                </div>
            </div>

            <!-- SIGNATURE BEFORE LOADING -->
            <div class="section">
                <div class="section-title">Signature (Before Supply)</div>

                <div class="flex flex-wrap w-full mt-4 justify-between">
                    <div class="w-full sm:w-1/2">
                        <div class="f-600">Terminal Representative</div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Name:</span>
                            <span class="ml-2">{{ signDisplay.before.terminal.name }}</span>
                        </div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Date and Time:</span>
                            <span class="ml-2">{{ signDisplay.before.terminal.createTime }}</span>
                        </div>

                        <div class="mt-4 f-600 flex" style="font-size: 12px">
                            <span>Signature:</span>
                            <img v-if="signDisplay.before.terminal.eSignature"
                                :src="getFullFileUrl(signDisplay.before.terminal.eSignature)" class="ml-2"
                                style="width: 100px;" />
                        </div>
                    </div>

                    <div class="w-full mt-4 sm:w-1/2 sm:mt-0">
                        <div class="f-600">Barge Representative</div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Name:</span>
                            <span class="ml-2">{{ signDisplay.before.barge.name }}</span>
                        </div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Date and Time:</span>
                            <span class="ml-2">{{ signDisplay.before.barge.createTime }}</span>
                        </div>

                        <div class="mt-4 f-600 flex" style="font-size: 12px">
                            <span>Signature:</span>
                            <img v-if="signDisplay.before.barge.eSignature"
                                :src="getFullFileUrl(signDisplay.before.barge.eSignature)" class="ml-2"
                                style="width: 100px;" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- SIGNATURE AFTER LOADING -->
            <div class="section">
                <div class="section-title">Signature (After Supply)</div>

                <div class="flex flex-wrap w-full mt-4 justify-between">
                    <div class="w-full sm:w-1/2">
                        <div class="f-600">Terminal Representative</div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Name:</span>
                            <span class="ml-2">{{ signDisplay.after.terminal.name }}</span>
                        </div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Date and Time:</span>
                            <span class="ml-2">{{ signDisplay.after.terminal.createTime }}</span>
                        </div>

                        <div class="mt-4 f-600 flex" style="font-size: 12px">
                            <span>Signature:</span>
                            <img v-if="signDisplay.after.terminal.eSignature"
                                :src="getFullFileUrl(signDisplay.after.terminal.eSignature)" class="ml-2"
                                style="width: 100px;" />
                        </div>
                    </div>

                    <div class="w-full mt-4 sm:w-1/2 sm:mt-0">
                        <div class="f-600">Barge Representative</div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Name:</span>
                            <span class="ml-2">{{ signDisplay.after.barge.name }}</span>
                        </div>

                        <div class="mt-4 f-600" style="font-size: 12px">
                            <span>Date and Time:</span>
                            <span class="ml-2">{{ signDisplay.after.barge.createTime }}</span>
                        </div>

                        <div class="mt-4 f-600 flex" style="font-size: 12px">
                            <span>Signature:</span>
                            <img v-if="signDisplay.after.barge.eSignature"
                                :src="getFullFileUrl(signDisplay.after.barge.eSignature)" class="ml-2"
                                style="width: 100px;" />
                        </div>
                    </div>
                </div>
            </div>
        </el-form>
        <el-dialog class="freshWaterSignDialog" :visible.sync="signDialogVisible"
            :width="isMobileScreen ? '100%' : '750px'" title="Sign Fresh Water" :close-on-click-modal="false">
            <div v-loading="signSubmitLoading" element-loading-text=" ">
                <div v-if="currentSignTarget" class="signing-context-box">
                    <div class="signing-context-value">
                        {{ currentSigningLabel }}
                    </div>
                </div>
                <el-form ref="freshWaterSignForm" :model="signForm" :rules="signRules" label-position="top"
                    size="small">
                    <el-row>
                        <el-col :xs="24" :sm="6" style="padding-right: 5px; min-width: 200px;">
                            <el-form-item label="Name" prop="name">
                                <el-input v-model="signForm.name" />
                            </el-form-item>

                            <el-form-item label="Stamp" prop="stamp">
                                <div class="flex flex-col gap-y-2">
                                    <el-image v-if="previewStamp.show" style="width: 139px; height: 139px;"
                                        :src="previewStamp.url" fit="contain" />

                                    <el-upload ref="freshWaterStampUpload" action="#" :show-file-list="false"
                                        accept=".png,.jpg,.jpeg" :http-request="uploadFreshWaterStamp"
                                        :before-upload="beforeStampUpload">
                                        <el-button round size="small" slot="trigger">
                                            {{ signForm.stamp ? "Update Stamp" : "Add Stamp" }}
                                        </el-button>
                                    </el-upload>

                                    <div class="text-xs" v-if="stampFileSize">
                                        Size: {{ stampFileSize }}
                                    </div>
                                </div>
                            </el-form-item>
                        </el-col>

                        <el-col :xs="24" :sm="18" style="padding-left: 5px; max-width: 500px;">
                            <el-form-item label="E-Signature" prop="eSignature">
                                <div class="signature-draw-wrapper">
                                    <div class="signature-clear"
                                        v-show="!showSavedSignature || updatePicbtnStatus === 1"
                                        @click="clearFreshWaterSign">
                                        <svg-icon iconClass="clear" />
                                    </div>

                                    <img v-show="showSavedSignature && updatePicbtnStatus === 0"
                                        :src="getFullFileUrl(userInfo.signPicture)" class="saved-signature-image" />

                                    <vue-sign v-show="!showSavedSignature || updatePicbtnStatus === 1"
                                        ref="freshWaterSignRef" :width="isMobileScreen ? 300 : 500"
                                        :height="isMobileScreen ? 200 : 300" :isCrop="isMobileScreen" />

                                    <div v-if="showSavedSignature && updatePicbtnStatus === 0" class="signature-clear"
                                        @click="handleUpdateFreshWaterSignatureClick">
                                        <svg-icon iconClass="clear" />
                                    </div>
                                </div>
                            </el-form-item>
                        </el-col>
                    </el-row>
                </el-form>

                <div class="flex justify-end">
                    <el-button size="small" @click="signDialogVisible = false">
                        Cancel
                    </el-button>
                    <el-button size="small" type="primary" :loading="signSubmitLoading" @click="submitFreshWaterSign">
                        Submit
                    </el-button>
                </div>
            </div>
        </el-dialog>

        <el-dialog :visible.sync="imagePreviewDialogVisible" :width="isMobileScreen ? '90vw' : '500px'" title="Preview">
            <div class="text-center w-full">
                <img :src="imagePreviewUrl" alt="Preview Image" style="margin: 0 auto; max-width: 100%;" />
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapState } from "vuex";
import VueSign from "@/components/vue-sign.vue";
import SvgIcon from "@/components/SvgIcon.vue";
import { getServerUrl } from "@/utils/getServerUrl";
import { singleleUploadFile } from '@/services';
import { createFreshWater, getFreshWaterById, getFreshWaterByNomId, updateFreshWater, signFreshWater } from '@/services/form';
import imageCompression from "browser-image-compression";
import axios from "axios";
import { getMvpToken } from "@/utils/auth";

export default {
    name: "FreshWaterForm",
    components: {
        VueSign,
        SvgIcon,
    },
    props: {
        createdtable: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            detailLoading: false,
            hasFreshWaterDetailLoaded: false,
            beforeReadingFileList: [],
            afterReadingFileList: [],
            pictureUploading: false,
            freshWaterStatus: null,
            freshWaterSignList: [],
            form: {
                id: "",
                serialNo: "",
                supplierName: "JURONG PORT UNIVERSAL TERMINAL",
                accountNo: "90178V",
                vesselName: "",
                imo: "",
                meterNumber: "",
                locationOfVessel: "",
                supplyStartDateTime: "",
                readingBeforeSupply: "",
                supplyEndDateTime: "",
                readingAfterSupply: "",
                quantity: "",
                billingPartyName: "",
                billingPartyCode: "",
                remarks: "",

                beforeLoadingTerminalName: "",
                beforeLoadingTerminalDateTime: "",
                beforeLoadingTerminalSignature: "",
                beforeLoadingBargeName: "",
                beforeLoadingBargeDateTime: "",
                beforeLoadingBargeSignature: "",

                afterLoadingTerminalName: "",
                afterLoadingTerminalDateTime: "",
                afterLoadingTerminalSignature: "",
                afterLoadingBargeName: "",
                afterLoadingBargeDateTime: "",
                afterLoadingBargeSignature: "",
                pictures: [],
            },
            signDialogVisible: false,
            signSubmitLoading: false,
            currentSignTarget: null,

            signForm: {
                name: "",
                stamp: "",
                eSignature: "",
            },
            previewStamp: {
                show: false,
                url: "",
            },
            stampFileSize: "",
            signRules: {
                name: [
                    { required: true, message: "Please input name", trigger: "blur" },
                ],
                stamp: [
                    { required: true, message: "Stamp required", trigger: "change" },
                ],
                eSignature: [
                    { required: true, message: "Signature required", trigger: "change" },
                ],
            },
            updatePicbtnStatus: 0,
            signDisplay: {
                before: {
                    terminal: {
                        name: "",
                        createTime: "",
                        eSignature: "",
                    },
                    barge: {
                        name: "",
                        createTime: "",
                        eSignature: "",
                    },
                },
                after: {
                    terminal: {
                        name: "",
                        createTime: "",
                        eSignature: "",
                    },
                    barge: {
                        name: "",
                        createTime: "",
                        eSignature: "",
                    },
                },
            },
            mvpToken: null,
            showBargeAuthTips: false,
            imagePreviewDialogVisible: false,
            imagePreviewUrl: "",
            savedFreshWaterForm: null,
        };
    },

    computed: {
        canEditBeforeReadings() {
            if (!this.canSaveFreshWaterRole) return false;
            if (this.isFreshWaterFullyCompleted) return false;
            return !this.beforeReadingSigned;
        },

        canEditAfterReadings() {
            if (!this.canSaveFreshWaterRole) return false;
            if (this.isFreshWaterFullyCompleted) return false;
            return this.beforeReadingSigned && !this.afterReadingSigned;
        },

        canEditMainForm() {
            if (!this.canSaveFreshWaterRole) return false;
            return !this.isFreshWaterFullyCompleted;
        },

        canEditSupplyDetails() {
            if (!this.canSaveFreshWaterRole) return false;
            return !this.isFreshWaterFullyCompleted;
        },

        canEditRemarks() {
            if (!this.canSaveFreshWaterRole) return false;
            return !this.isFreshWaterFullyCompleted;
        },

        canEditTerminalSignature() {
            return true;
        },

        canEditBargeSignature() {
            return true;
        },

        hasFreshWaterSignAction() {
            return (
                this.canSignBeforeTerminal ||
                this.canSignBeforeBarge ||
                this.canSignAfterTerminal ||
                this.canSignAfterBarge
            );
        },
        ...mapState(["userInfo", "screenType"]),

        isMobileScreen() {
            return this.screenType?.isMobileScreen ?? false;
        },

        currentRole() {
            return String(this.userInfo?.role || "");
        },

        currentUserType() {
            return String(this.userInfo?.userType || "");
        },

        isSignatureEnabled() {
            return this.freshWaterStatus !== null &&
                this.freshWaterStatus !== undefined &&
                [0, 1].includes(Number(this.freshWaterStatus));
        },

        canCurrentUserSign() {
            return this.isSignatureEnabled && this.canSignFreshWaterRole;
        },

        isTerminalSigner() {
            return ["3", "11"].includes(this.currentRole);
        },

        isBargeSigner() {
            return this.currentUserType === "4" && this.currentRole === "8";
        },

        canSignBeforeTerminal() {
            if (this.isLogisticsAtCompletedStatus) {
                return this.isSignatureEnabled &&
                    this.isSavedBeforeReadingCompleted &&
                    !this.hasSigned(1, 2);
            }
            return this.canCurrentUserSign &&
                this.isSavedBeforeReadingCompleted &&
                this.isTerminalSigner &&
                !this.beforeReadingSigned &&
                !this.hasSigned(1, 2);
        },

        canSignBeforeBarge() {
            return this.canCurrentUserSign &&
                this.isSavedBeforeReadingCompleted &&
                this.isBargeSigner &&
                !this.beforeReadingSigned &&
                !this.hasSigned(1, 1);
        },

        canSignAfterTerminal() {
            if (this.isLogisticsAtCompletedStatus) {
                return this.isSignatureEnabled &&
                    this.beforeReadingSigned &&
                    this.isSavedAfterReadingCompleted &&
                    !this.hasSigned(2, 2) &&
                    Number(this.freshWaterStatus) === 1;
            }
            return this.canCurrentUserSign &&
                this.beforeReadingSigned &&
                this.isSavedAfterReadingCompleted &&
                !this.afterReadingSigned &&
                this.isTerminalSigner &&
                !this.hasSigned(2, 2) &&
                Number(this.freshWaterStatus) === 1;
        },

        canSignAfterBarge() {
            return this.canCurrentUserSign &&
                this.beforeReadingSigned &&
                this.isSavedAfterReadingCompleted &&
                !this.afterReadingSigned &&
                this.isBargeSigner &&
                !this.hasSigned(2, 1) &&
                Number(this.freshWaterStatus) === 1;
        },

        showSavedSignature() {
            return !!this.userInfo?.signPicture;
        },

        beforeReadingSigned() {
            return this.isSignTypeCompleted(1);
        },

        afterReadingSigned() {
            return this.isSignTypeCompleted(2);
        },

        isAfterReadingCompleted() {
            const hasEndDate = !!this.form.supplyEndDateTime;
            const hasAfterReading =
                this.form.readingAfterSupply !== "" &&
                this.form.readingAfterSupply !== null &&
                this.form.readingAfterSupply !== undefined;

            const hasAfterPicture = (this.form.pictures || []).some(
                item => Number(item.pictureType) === 2 && item.pictureUrl
            );

            return hasEndDate && hasAfterReading && hasAfterPicture;
        },

        isLogisticsAtCompletedStatus() {
            return Number(this.createdtable?.status) === 4 && this.currentRole === "1";
        },

        canSaveFreshWaterRole() {
            const status = Number(this.createdtable?.status);
            const role = this.currentRole;

            // After completed: logistics only, until form fully completed
            if (status === 4) return role === "1" && !this.isFreshWaterFullyCompleted;
            // Before completed (in-progress): leader and loading master
            if (status === 3) return ["3", "11"].includes(role);
            // Booked: leader and loading master
            if (status === 2) return ["3", "11"].includes(role);

            return ["3", "11"].includes(role);
        },

        canSignFreshWaterRole() {
            const status = Number(this.createdtable?.status);
            if (status === 4) return ["1", "8"].includes(this.currentRole);
            return ["3", "11", "8"].includes(this.currentRole);
        },

        isBaseFreshWaterCompleted() {
            return (
                !!this.form.supplierName &&
                !!this.form.accountNo &&
                !!this.form.vesselName &&
                !!this.form.imo &&
                !!this.form.meterNumber &&
                !!this.form.serialNo &&
                !!this.form.locationOfVessel
            );
        },

        isBeforeReadingCompleted() {
            const hasStartDate = !!this.form.supplyStartDateTime;

            const hasBeforeReading =
                this.form.readingBeforeSupply !== "" &&
                this.form.readingBeforeSupply !== null &&
                this.form.readingBeforeSupply !== undefined;

            const hasBeforePicture = (this.form.pictures || []).some(
                item => Number(item.pictureType) === 1 && item.pictureUrl
            );

            return (
                this.isBaseFreshWaterCompleted &&
                hasStartDate &&
                hasBeforeReading &&
                hasBeforePicture
            );
        },

        isAfterReadingCompleted() {
            const hasEndDate = !!this.form.supplyEndDateTime;

            const hasAfterReading =
                this.form.readingAfterSupply !== "" &&
                this.form.readingAfterSupply !== null &&
                this.form.readingAfterSupply !== undefined;

            const hasQuantity =
                this.form.quantity !== "" &&
                this.form.quantity !== null &&
                this.form.quantity !== undefined;

            const hasAfterPicture = (this.form.pictures || []).some(
                item => Number(item.pictureType) === 2 && item.pictureUrl
            );

            return (
                this.isBeforeReadingCompleted &&
                hasEndDate &&
                hasAfterReading &&
                hasQuantity &&
                hasAfterPicture
            );
        },
        isSavedBaseFreshWaterCompleted() {
            const saved = this.savedFreshWaterForm;

            if (!saved || !saved.id) return false;

            return (
                this.hasSavedValue(saved.supplierName) &&
                this.hasSavedValue(saved.accountNo) &&
                this.hasSavedValue(saved.vesselName) &&
                this.hasSavedValue(saved.imo) &&
                this.hasSavedValue(saved.meterNumber) &&
                this.hasSavedValue(saved.serialNo) &&
                this.hasSavedValue(saved.locationOfVessel) &&
                this.hasSavedValue(saved.billingPartyName) &&
                this.hasSavedValue(saved.billingPartyCode)
            );
        },

        isSavedBeforeReadingCompleted() {
            const saved = this.savedFreshWaterForm;

            if (!saved || !saved.id) return false;

            return (
                this.isSavedBaseFreshWaterCompleted &&
                this.hasSavedValue(saved.supplyStartDateTime) &&
                this.hasSavedValue(saved.readingBeforeSupply) &&
                this.hasSavedPicture(1)
            );
        },

        isSavedAfterReadingCompleted() {
            const saved = this.savedFreshWaterForm;

            if (!saved || !saved.id) return false;

            return (
                this.isSavedBeforeReadingCompleted &&
                this.hasSavedValue(saved.supplyEndDateTime) &&
                this.hasSavedValue(saved.readingAfterSupply) &&
                this.hasSavedValue(saved.quantity) &&
                this.hasSavedPicture(2)
            );
        },

        isFreshWaterFullyCompleted() {
            return this.beforeReadingSigned && this.afterReadingSigned;
        },
        currentSigningLabel() {
            if (!this.currentSignTarget) return "";

            const stageLabel =
                this.currentSignTarget.stage === "after"
                    ? "After Supply"
                    : "Before Supply";

            const partyLabel =
                this.currentSignTarget.party === "barge"
                    ? "Barge Representative"
                    : "Terminal Representative";

            return `${stageLabel} - ${partyLabel}`;
        },
    },

    watch: {
        createdtable: {
            immediate: true,
            deep: true,
            handler() {
                this.hasFreshWaterDetailLoaded = false;
                this.mapCreatedTable(this.createdtable || {});
                this.loadFreshWaterDetail();
            }
        },

        "form.supplyStartDateTime"() {
            this.emitFreshWaterSignAvailability();
        },

        "form.readingBeforeSupply"() {
            this.calculateQuantity();
            this.emitFreshWaterSignAvailability();
        },

        "form.supplyEndDateTime"() {
            this.emitFreshWaterSignAvailability();
        },

        "form.readingAfterSupply"() {
            this.calculateQuantity();
            this.emitFreshWaterSignAvailability();
        },

        "form.quantity"() {
            this.emitFreshWaterSignAvailability();
        },

        "form.pictures": {
            deep: true,
            handler() {
                this.emitFreshWaterSignAvailability();
            },
        },

        hasFreshWaterSignAction: {
            immediate: true,
            handler(val) {
                this.$emit("fresh-water-sign-available", val);
            }
        },

        freshWaterSignList: {
            immediate: true,
            deep: true,
            handler(val) {
                const list = Array.isArray(val) ? val : [];
                this.$emit("fresh-water-sign-state", {
                    hasBeforeTerminalSigned: list.some(i => Number(i.signType) === 1 && Number(i.type) === 2),
                    hasAfterTerminalSigned: list.some(i => Number(i.signType) === 2 && Number(i.type) === 2),
                    hasBeforeBargeSign: list.some(i => Number(i.signType) === 1 && Number(i.type) === 1),
                    hasAfterBargeSign: list.some(i => Number(i.signType) === 2 && Number(i.type) === 1),
                });
            }
        },
    },

    methods: {
        // Future datetime is allowed, but remind the user to double-check (value kept).
        remindIfFuture(val, label) {
            if (val && new Date(val).getTime() > Date.now()) {
                this.$alert(
                    label + " is in the future. Please double-check.",
                    "Reminder",
                    { type: "warning" }
                );
            }
        },
        onSupplyStartChange(val) {
            this.remindIfFuture(val, "Supply Start Date and Time");
        },
        onSupplyEndChange(val) {
            // End earlier than start is not allowed: popup and clear.
            const start = this.form.supplyStartDateTime;
            if (val && start && new Date(val).getTime() < new Date(start).getTime()) {
                this.$alert(
                    "Supply End Date and Time cannot be earlier than the Supply Start Date and Time.",
                    "Invalid date",
                    { type: "error" }
                );
                this.form.supplyEndDateTime = "";
                return;
            }
            this.remindIfFuture(val, "Supply End Date and Time");
        },
        mapCreatedTable(data) {
            this.form.supplierName =
                data.supplierName ||
                this.form.supplierName ||
                "JURONG PORT UNIVERSAL TERMINAL";
            this.form.serialNo = data.serialNo || data.serialNumber || "";
            this.form.vesselName = data.shipName || data.vesselName || "";
            this.form.imo = data.imo || data.bargeName || "";
            this.form.accountNo = data.accountNo || "90178V";
            const rawLocation = data.jettyNo || data.locationOfVessel || "";
            this.form.locationOfVessel = rawLocation && !rawLocation.startsWith("OMU") ? "OMU " + rawLocation : rawLocation || "OMU";
            this.form.meterNumber = data.waterMeter || data.meterNumber || "";
            this.form.remarks = data.waterRemarks || "";
            this.form.billingPartyName =
                data.billingName ||
                data.billingPartyName ||
                "";

            this.form.billingPartyCode =
                data.billingCode ||
                data.billingPartyCode ||
                "";
        },

        getDecimalLength(value) {
            const text = String(value);

            if (!text.includes(".")) {
                return 0;
            }

            return text.split(".")[1].length;
        },

        safeDecimalSubtract(afterValue, beforeValue) {
            const afterText = String(afterValue);
            const beforeText = String(beforeValue);

            const decimalLength = Math.max(
                this.getDecimalLength(afterText),
                this.getDecimalLength(beforeText)
            );

            const multiplier = Math.pow(10, decimalLength);

            const afterInteger = Math.round(Number(afterText) * multiplier);
            const beforeInteger = Math.round(Number(beforeText) * multiplier);

            const result = (afterInteger - beforeInteger) / multiplier;

            return this.trimTrailingZeros(result.toFixed(decimalLength));
        },

        trimTrailingZeros(value) {
            const str = String(value);
            if (!str.includes(".")) return str;
            return str.replace(/\.?0+$/, "");
        },

        calculateQuantity() {
            const beforeRaw = this.form.readingBeforeSupply;
            const afterRaw = this.form.readingAfterSupply;

            if (
                beforeRaw === "" ||
                beforeRaw === null ||
                beforeRaw === undefined ||
                afterRaw === "" ||
                afterRaw === null ||
                afterRaw === undefined
            ) {
                this.form.quantity = "";
                return;
            }

            const before = Number(beforeRaw);
            const after = Number(afterRaw);

            if (Number.isNaN(before) || Number.isNaN(after) || after < before) {
                this.form.quantity = "";
                return;
            }

            this.form.quantity = this.safeDecimalSubtract(afterRaw, beforeRaw);
        },

        isFreshWaterCreated() {
            return this.form.id !== undefined &&
                this.form.id !== null &&
                this.form.id !== "";
        },

        submitForm(callback) {
            this.$refs.freshWaterForm.validate(async (valid) => {
                if (!valid) return;

                if (this.isFreshWaterFullyCompleted) {
                    this.$message.warning("Fresh Water form has been completed and cannot be edited");
                    return;
                }

                if (!this.canSaveFreshWaterRole) {
                    this.$message.warning("You are not allowed to save Fresh Water form");
                    return;
                }

                if (this.pictureUploading) {
                    this.$message.warning("Please wait for picture upload to finish");
                    return;
                }

                const payload = this.buildFreshWaterPayload();

                if (!payload.nominationId) {
                    this.$message.error("Nomination ID is required");
                    return;
                }

                callback && callback();

                try {
                    const res = this.isFreshWaterCreated()
                        ? await updateFreshWater(
                            this.form.id,
                            { ...payload }
                        )
                        : await createFreshWater(payload);

                    if (res.code === 200) {
                        const savedId =
                            res?.data?.id ||
                            res?.data ||
                            this.form.id;

                        if (savedId) {
                            this.form.id = savedId;
                            await this.loadFreshWaterDetailById(savedId);
                        }

                        this.$message.success(
                            this.isFreshWaterCreated()
                                ? "Fresh Water detail updated successfully"
                                : "Fresh Water detail created successfully"
                        );

                        this.$emit("upup", "1");
                    } else {
                        this.$message.error(res.msg || "Failed to save fresh water detail");
                    }
                } catch (error) {
                    console.error(error);
                    this.$message.error("Failed to save fresh water detail");
                } finally {
                    this.$emit("loading", "1");
                }
            });
        },

        buildFreshWaterPayload() {
            return {
                supplierName: this.form.supplierName,
                accountNo: this.form.accountNo,
                vesselName: this.form.vesselName,
                imo: this.form.imo,
                meterNumber: this.form.meterNumber,
                locationOfVessel: (this.form.locationOfVessel || "").replace(/^OMU\s*/i, ""),
                serialNo: this.form.serialNo,
                supplyStartDatetime: this.form.supplyStartDateTime,
                readingBeforeSupply: this.toNumberOrNull(this.form.readingBeforeSupply),

                supplyEndDatetime: this.form.supplyEndDateTime,
                readingAfterSupply: this.toNumberOrNull(this.form.readingAfterSupply),
                quantity: this.toNumberOrNull(this.form.quantity),
                billingName: this.form.billingPartyName,
                billingCode: this.form.billingPartyCode,

                remarks: this.form.remarks,
                nominationId: Number(this.getNominationId()),

                pictures: this.normalizePictures(this.form.pictures),
            };
        },

        beforePictureUpload(file) {
            const isImage = file.type && file.type.indexOf("image/") === 0;
            const isLt10M = file.size / 1024 / 1024 < 10;

            if (!isImage) {
                this.$message.error("Only image files are allowed");
                return false;
            }

            if (!isLt10M) {
                this.$message.error("Image size cannot exceed 10MB");
                return false;
            }

            return true;
        },

        async compressPictureIfNeeded(file) {
            const maxSizeMB = 1;
            const fileSizeMB = file.size / 1024 / 1024;

            if (fileSizeMB <= maxSizeMB) {
                return file;
            }

            return await imageCompression(file, {
                maxSizeMB,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: file.type || "image/jpeg",
                initialQuality: 0.8,
            });
        },

        getNominationId() {
            return (
                this.createdtable?.bid ||
                this.$route.query.id
            );
        },

        toNumberOrNull(value) {
            if (value === "" || value === null || value === undefined) {
                return null;
            }

            const numberValue = Number(value);
            return Number.isNaN(numberValue) ? null : numberValue;
        },

        async loadFreshWaterDetail() {
            const nominationId = this.getNominationId();

            if (!nominationId) return;

            this.detailLoading = true;

            try {
                const res = await getFreshWaterByNomId({
                    nominationId,
                });

                const summary = this.normalizeFreshWaterDetail(res);

                this.applyFreshWaterPermissionState(res, summary);
                this.mapCreatedTable(this.createdtable || {});

                if (!summary || !summary.id) {
                    this.form.id = "";
                    this.freshWaterStatus = null;
                    this.freshWaterSignList = [];
                    this.savedFreshWaterForm = null;
                    this.resetSignDisplay();
                    this.hasFreshWaterDetailLoaded = false;

                    this.$nextTick(() => {
                        this.$emit("fresh-water-sign-available", this.hasFreshWaterSignAction);
                    });

                    return;
                }

                this.form.id = summary.id;

                await this.loadFreshWaterDetailById(summary.id);

            } catch (error) {
                console.error(error);
                this.$message.error("Failed to load fresh water detail");
            } finally {
                this.detailLoading = false;
            }
        },

        async loadFreshWaterDetailById(id) {
            if (!id) return;

            const res = await getFreshWaterById(
                id,
            );

            const detail = this.normalizeFreshWaterDetail(res);

            this.applyFreshWaterPermissionState(res, detail);
            this.mapCreatedTable(this.createdtable || {});

            if (!detail || Object.keys(detail).length === 0) {
                this.hasFreshWaterDetailLoaded = false;
                return;
            }

            this.applyFreshWaterDetail(detail);
            this.hasFreshWaterDetailLoaded = true;
            this.calculateQuantity();

            this.savedFreshWaterForm = JSON.parse(JSON.stringify(this.form));
            this.$nextTick(() => {
                this.$emit("fresh-water-sign-available", this.hasFreshWaterSignAction);
            });
        },

        applyFreshWaterPermissionState(res, detail) {
            const data = res?.data?.data || res?.data || detail || {};

            this.freshWaterStatus =
                data.status !== undefined && data.status !== null
                    ? data.status
                    : null;

            this.freshWaterSignList = Array.isArray(data.signList)
                ? data.signList
                : [];

            this.applySignListToDisplay(this.freshWaterSignList);
        },

        normalizeFreshWaterDetail(res) {
            const raw = res?.data !== undefined ? res.data : res;
            const data = raw?.data !== undefined ? raw.data : raw;

            if (!data) return null;

            if (Array.isArray(data)) {
                return data[0] || null;
            }

            if (Array.isArray(data.rows)) {
                return data.rows[0] || null;
            }

            return data;
        },

        applyFreshWaterDetail(data) {
            const fieldMap = {
                id: ["id", "freshWaterId", "waterVoucherId", "voucherId"],
                serialNo: ["serialNo", "serialNumber"],
                supplierName: ["supplierName"],
                accountNo: ["accountNo"],
                vesselName: ["vesselName", "shipName"],
                imo: ["imo", "bargeName"],
                meterNumber: ["meterNumber", "waterMeter", "serialNumber"],
                locationOfVessel: ["jettyNo", "locationOfVessel", "berthName"],

                supplyStartDateTime: ["supplyStartDatetime", "startDateTime", "supplyStartTime"],
                readingBeforeSupply: ["readingBeforeSupply", "beforeReading"],
                supplyEndDateTime: ["supplyEndDatetime", "endDateTime", "supplyEndTime"],
                readingAfterSupply: ["readingAfterSupply", "afterReading"],
                quantity: ["quantitySupplied", "quantity"],

                billingPartyName: ["billingName", "billingPartyName"],
                billingPartyCode: ["billingCode", "billingPartyCode"],
                remarks: ["remarks", "waterRemarks"],

                beforeLoadingTerminalName: ["beforeLoadingTerminalName"],
                beforeLoadingTerminalDateTime: ["beforeLoadingTerminalDateTime"],
                beforeLoadingTerminalSignature: ["beforeLoadingTerminalSignature", "beforeLoadingTerminalSign"],

                beforeLoadingBargeName: ["beforeLoadingBargeName"],
                beforeLoadingBargeDateTime: ["beforeLoadingBargeDateTime"],
                beforeLoadingBargeSignature: ["beforeLoadingBargeSignature", "beforeLoadingBargeSign"],

                afterLoadingTerminalName: ["afterLoadingTerminalName"],
                afterLoadingTerminalDateTime: ["afterLoadingTerminalDateTime"],
                afterLoadingTerminalSignature: ["afterLoadingTerminalSignature", "afterLoadingTerminalSign"],

                afterLoadingBargeName: ["afterLoadingBargeName"],
                afterLoadingBargeDateTime: ["afterLoadingBargeDateTime"],
                afterLoadingBargeSignature: ["afterLoadingBargeSignature", "afterLoadingBargeSign"],
            };

            Object.keys(fieldMap).forEach((formKey) => {
                const value = this.pickValue(data, fieldMap[formKey]);

                if (value !== undefined && value !== null) {
                    this.form[formKey] = value;
                }
            });

            if (Array.isArray(data.pictures)) {
                this.form.pictures = data.pictures.map(item => ({
                    pictureType: Number(item.pictureType),
                    pictureUrl: this.getFullFileUrl(item.pictureUrl),
                }));

                this.normalizePictureFileList();
            }

            if (!this.form.supplierName) {
                this.form.supplierName = "JURONG PORT UNIVERSAL TERMINAL";
            }

            if (this.createdtable?.jettyNo) {
                const jetty = this.createdtable.jettyNo;
                this.form.locationOfVessel = jetty.startsWith("OMU") ? jetty : "OMU " + jetty;
            }
        },

        normalizePictureFileList() {
            const pictures = Array.isArray(this.form.pictures)
                ? this.form.pictures
                : [];

            const beforePicture = pictures.find(
                item => Number(item.pictureType) === 1
            );

            const afterPicture = pictures.find(
                item => Number(item.pictureType) === 2
            );

            this.beforeReadingFileList = beforePicture?.pictureUrl
                ? [{ name: "Before Reading Picture", url: this.getFullFileUrl(beforePicture.pictureUrl) }]
                : [];

            this.afterReadingFileList = afterPicture?.pictureUrl
                ? [{ name: "After Reading Picture", url: this.getFullFileUrl(afterPicture.pictureUrl) }]
                : [];
        },

        pickValue(source, keys) {
            for (const key of keys) {
                if (source[key] !== undefined && source[key] !== null) {
                    return source[key];
                }
            }

            return undefined;
        },

        handlePictureExceed() {
            this.$message.warning("Only one picture can be uploaded for each reading");
        },

        uploadReadingPicture(options, pictureType) {
            const { file, onSuccess, onError } = options;

            let aborted = false;

            this.pictureUploading = true;

            const runUpload = async () => {
                try {
                    const uploadFile = await this.compressPictureIfNeeded(file);

                    if (aborted) return;

                    const res = await singleleUploadFile(uploadFile);
                    if (aborted) return;

                    const pictureUrl = this.getUploadPictureUrl(res);

                    if (!pictureUrl) {
                        throw new Error("Upload succeeded but picture URL is empty");
                    }

                    this.upsertPicture({
                        pictureType,
                        pictureUrl,
                    });

                    const fileItem = {
                        name: file.name,
                        url: this.getFullFileUrl(pictureUrl),
                    };

                    if (Number(pictureType) === 1) {
                        this.beforeReadingFileList = [fileItem];
                    }

                    if (Number(pictureType) === 2) {
                        this.afterReadingFileList = [fileItem];
                    }

                    onSuccess && onSuccess(res, file);
                    this.$message.success("Picture uploaded successfully");
                } catch (error) {
                    if (!aborted) {
                        console.error(error);
                        this.handlePictureRemove(pictureType);
                        onError && onError(error);
                        this.$message.error("Failed to upload picture");
                    }
                } finally {
                    this.pictureUploading = false;

                }
            };

            runUpload();

            return {
                abort: () => {
                    aborted = true;
                    this.pictureUploading = false;
                },
            };
        },

        handlePictureRemove(pictureType) {
            this.form.pictures = this.form.pictures.filter(
                item => Number(item.pictureType) !== Number(pictureType)
            );

            if (Number(pictureType) === 1) {
                this.beforeReadingFileList = [];
            }

            if (Number(pictureType) === 2) {
                this.afterReadingFileList = [];
            }
        },

        upsertPicture(picture) {
            const pictures = Array.isArray(this.form.pictures)
                ? [...this.form.pictures]
                : [];

            const index = pictures.findIndex(
                item => Number(item.pictureType) === Number(picture.pictureType)
            );

            if (index > -1) {
                pictures.splice(index, 1, picture);
            } else {
                pictures.push(picture);
            }

            this.form.pictures = pictures;
        },

        getUploadPictureUrl(res) {
            return (
                res?.fileName ||
                res?.url ||
                res?.data?.url ||
                res?.data?.fileName ||
                res?.fileName ||
                res?.data?.pictureUrl ||
                res?.pictureUrl ||
                ""
            );
        },

        normalizePictures(pictures) {
            if (!Array.isArray(pictures)) return [];

            return pictures
                .filter(item => item && item.pictureUrl)
                .map(item => ({
                    pictureType: Number(item.pictureType),
                    pictureUrl: item.pictureUrl,
                }));
        },

        openAvailableSignDialog() {
            if (!this.canSignFreshWaterRole) {
                this.$message.warning("You are not allowed to sign Fresh Water form");
                return;
            }
            if (this.canSignBeforeTerminal) {
                this.openFreshWaterSignDialog("before", "terminal");
                return;
            }

            if (this.canSignBeforeBarge) {
                this.openFreshWaterSignDialog("before", "barge");
                return;
            }

            if (this.canSignAfterTerminal) {
                this.openFreshWaterSignDialog("after", "terminal");
                return;
            }

            if (this.canSignAfterBarge) {
                this.openFreshWaterSignDialog("after", "barge");
                return;
            }

            this.$message.warning("No signature action available");
        },

        hasSigned(signType, type) {
            return (this.freshWaterSignList || []).some(item =>
                Number(item.signType) === Number(signType) &&
                Number(item.type) === Number(type)
            );
        },

        getSignTypeByStage(stage) {
            return stage === "after" ? 2 : 1;
        },

        getSignerTypeByParty(party) {
            return party === "barge" ? 1 : 2;
        },

        getFullFileUrl(url) {
            if (!url) return "";
            if (/^https?:\/\//.test(url)) return url;
            return getServerUrl() + url;
        },

        async openFreshWaterSignDialog(stage, party) {
            const signType = this.getSignTypeByStage(stage);
            const type = this.getSignerTypeByParty(party);

            if (this.hasSigned(signType, type)) {
                this.$message.warning("This signature has already been submitted");
                return;
            }

            this.currentSignTarget = {
                stage,
                party,
                signType,
                type,
            };

            this.signForm = {
                name: this.userInfo?.nickName || this.userInfo?.userName || "",
                stamp: "",
                eSignature: "",
            };

            this.previewStamp = {
                show: false,
                url: "",
            };

            this.updatePicbtnStatus = 1;
            this.stampFileSize = "";

            if (this.currentUserType === "4") {
                this.mvpToken = getMvpToken();

                if (!this.mvpToken) {
                    this.$message.warning("Authentication is required to sign. Please re-authenticate.");
                } else {
                    await this.getBargeStamp();
                }
            }
            this.signForm.stamp = this.userInfo?.stamp || "";
            this.signForm.eSignature = this.userInfo?.signPicture || "";

            this.previewStamp = {
                show: !!this.userInfo?.stamp,
                url: this.userInfo?.stamp ? this.getFullFileUrl(this.userInfo.stamp) : "",
            };

            this.updatePicbtnStatus = this.userInfo?.signPicture ? 0 : 1;


            this.signDialogVisible = true;

            this.$nextTick(() => {
                this.$refs.freshWaterSignForm?.clearValidate();

                if (!this.signForm.eSignature) {
                    this.$refs.freshWaterSignRef?.reset();
                }
            });
        },
        clearFreshWaterSign() {
            this.$refs.freshWaterSignRef?.reset();
            this.signForm.eSignature = "";
        },

        async getFreshWaterSignFile() {
            const res = await this.$refs.freshWaterSignRef.generate();

            const arr = res.split(",");
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new File([u8arr], `fresh-water-signature-${Date.now()}.png`, {
                type: mime,
            });
        },

        beforeStampUpload(file) {
            this.stampFileSize = `${(file.size / 1000000).toPrecision(4)}MB`;

            const isImage = ["image/png", "image/jpeg", "image/jpg"].includes(file.type);
            const isLt2M = file.size / 1024 / 1024 < 2;

            if (!isImage) {
                this.$message.error("Only JPEG/PNG files are allowed");
                return false;
            }

            if (!isLt2M) {
                this.$message.error("JPEG/PNG file size must be less than 2MB");
                return false;
            }

            return true;
        },

        uploadFreshWaterStamp(options) {
            const { file, onSuccess, onError } = options;
            let aborted = false;

            const runUpload = async () => {
                try {
                    if (aborted) return;

                    let res;
                    res = await singleleUploadFile(file);
                    if (aborted) return;
                    const stampUrl = this.getUploadPictureUrl(res);
                    if (!stampUrl) {
                        throw new Error("Stamp upload succeeded but URL is empty");
                    }
                    this.signForm.stamp = stampUrl;
                    this.$set(this.userInfo, "stamp", stampUrl);
                    this.previewStamp = {
                        show: true,
                        url: this.getFullFileUrl(stampUrl),
                    };

                    this.$refs.freshWaterSignForm?.validateField("stamp");
                    onSuccess && onSuccess(res, file);
                } catch (error) {
                    if (!aborted) {
                        console.error(error);
                        onError && onError(error);
                        this.$message.error("Failed to upload stamp");
                    }
                }
            };

            runUpload();

            return {
                abort() {
                    aborted = true;
                },
            };
        },

        async submitFreshWaterSign() {
            if (!this.currentSignTarget) {
                this.$message.error("No signature target selected");
                return;
            }

            this.signSubmitLoading = true;
            this.$emit("fresh-water-sign-loading", true);

            try {
                let eSignature = this.signForm.eSignature;

                const shouldUseDrawnSignature =
                    !this.showSavedSignature || this.updatePicbtnStatus === 1;

                if (shouldUseDrawnSignature) {
                    const file = await this.getFreshWaterSignFile();
                    const uploadRes = await singleleUploadFile(file); // Always use singleleUploadFile
                    eSignature = this.getUploadPictureUrl(uploadRes);

                    if (!eSignature) {
                        this.$message.error("Failed to upload signature");
                        return;
                    }

                    this.signForm.eSignature = eSignature;
                }

                this.$refs.freshWaterSignForm.validate(async (valid) => {
                    if (!valid) {
                        this.signSubmitLoading = false;
                        this.$emit("fresh-water-sign-loading", false);
                        return;
                    }

                    const payload = {
                        signType: this.currentSignTarget.signType,
                        type: this.currentSignTarget.type,
                        eSignature: this.signForm.eSignature,
                        name: this.signForm.name,
                        stamp: this.signForm.stamp,
                        nominationId: Number(this.getNominationId()),
                    };

                    if (!payload.nominationId) {
                        this.$message.error("Nomination ID is required");
                        return;
                    }

                    try {
                        const res = await signFreshWater(payload);

                        if (res.code === 200) {
                            if (this.form.id) {
                                await this.loadFreshWaterDetailById(this.form.id);
                            } else {
                                await this.loadFreshWaterDetail();
                            }

                            this.$message.success("Fresh Water signed successfully");
                            this.signDialogVisible = false;
                            this.$emit("upup", "1");
                        } else {
                            this.$message.error(res.msg || "Failed to sign Fresh Water");
                        }
                    } catch (error) {
                        console.error(error);
                        this.$message.error("Failed to sign Fresh Water");
                    } finally {
                        this.signSubmitLoading = false;
                        this.$emit("fresh-water-sign-loading", false);
                    }
                });
            } catch (error) {
                console.error(error);
                this.$message.error("Failed to generate signature");
                this.signSubmitLoading = false;
                this.$emit("fresh-water-sign-loading", false);
            }
        },

        resetSignDisplay() {
            this.signDisplay = {
                before: {
                    terminal: { name: "", createTime: "", eSignature: "" },
                    barge: { name: "", createTime: "", eSignature: "" },
                },
                after: {
                    terminal: { name: "", createTime: "", eSignature: "" },
                    barge: { name: "", createTime: "", eSignature: "" },
                },
            };
        },

        applySignListToDisplay(signList) {
            this.resetSignDisplay();

            if (!Array.isArray(signList)) return;

            signList.forEach((item) => {
                const signType = Number(item.signType); // 1 = before, 2 = after
                const type = Number(item.type);         // 1 = barge, 2 = terminal

                const targetStage = signType === 2 ? "after" : "before";
                const targetParty = type === 1 ? "barge" : "terminal";

                this.signDisplay[targetStage][targetParty] = {
                    name: item.name || "",
                    createTime: item.createTime || item.signTime || item.dateTime || "",
                    eSignature: item.eSignature || item.esignature || item.signatureUrl || "",
                };
            });
        },

        handleUpdateFreshWaterSignatureClick() {
            this.updatePicbtnStatus = 1;
            this.signForm.eSignature = "";
            this.$nextTick(() => {
                this.$refs.freshWaterSignRef?.reset();
            });
        },

        async getBargeStamp() {
            if (!this.mvpToken) return;

            try {
                const res = await axios.get(
                    process.env.VUE_APP_MVP_BASE_URL + "/marinePass/doc/getShipStamp",
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: "Bearer " + this.mvpToken,
                        },
                    }
                );

                const data = res.data;

                if (data && data.stampUrl) {
                    this.previewStamp = {
                        show: true,
                        url: process.env.VUE_APP_MVP_BASE_URL + data.stampUrl,
                    };

                    this.signForm.stamp = data.stampUrl;
                }
            } catch (error) {
                console.error(error);
            }
        },

        async uploadFreshWaterSignToMvp(file) {
            const formData = new FormData();
            formData.append("file", file);

            try {
                const res = await axios.post(
                    process.env.VUE_APP_MVP_BASE_URL + "/common/upload",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            Authorization: "Bearer " + this.mvpToken,
                        },
                    }
                );

                const data = res.data;

                if (data.code === 401) {
                    this.$message.warning("Authentication is required to sign. Please re-authenticate.");
                }

                return data;
            } catch (error) {
                console.error(error);
                return null;
            }
        },
        getSignedCountBySignType(signType) {
            return (this.freshWaterSignList || []).filter(
                item => Number(item.signType) === Number(signType)
            ).length;
        },

        isSignTypeCompleted(signType) {
            return this.getSignedCountBySignType(signType) >= 2;
        },

        handleOpenReadingImageWindow(type) {
            if (type === "before") {
                this.$refs.beforeReadingImage?.click();
                return;
            }

            if (type === "after") {
                this.$refs.afterReadingImage?.click();
            }
        },

        handlePreviewReadingImage(pictureType) {
            const url = this.getReadingPictureUrl(pictureType);

            if (!url) return;

            this.imagePreviewUrl = url;
            this.imagePreviewDialogVisible = true;
        },

        async handleReadingFileChange(pictureType, event) {
            const files = event?.target?.files || [];

            if (!files.length) return;

            const file = files[0];

            if (this.beforePictureUpload(file) === false) {
                event.target.value = "";
                return;
            }

            this.pictureUploading = true;

            try {
                const uploadFile = await this.compressPictureIfNeeded(file);
                const res = await singleleUploadFile(uploadFile);
                const pictureUrl = this.getUploadPictureUrl(res);

                if (!pictureUrl) {
                    throw new Error("Upload succeeded but picture URL is empty");
                }

                this.upsertPicture({
                    pictureType: Number(pictureType),
                    pictureUrl,
                });

                if (Number(pictureType) === 1) {
                    this.beforeReadingFileList = [
                        {
                            name: file.name,
                            url: this.getFullFileUrl(pictureUrl),
                        },
                    ];
                }

                if (Number(pictureType) === 2) {
                    this.afterReadingFileList = [
                        {
                            name: file.name,
                            url: this.getFullFileUrl(pictureUrl),
                        },
                    ];
                }

                this.$message.success("Picture uploaded successfully");
            } catch (error) {
                console.error(error);
                this.$message.error("Failed to upload picture");
            } finally {
                this.pictureUploading = false;
                event.target.value = "";
            }
        },

        getReadingPictureUrl(pictureType) {
            const picture = (this.form.pictures || []).find(
                item => Number(item.pictureType) === Number(pictureType)
            );

            if (!picture || !picture.pictureUrl) return "";

            return this.getFullFileUrl(picture.pictureUrl);
        },

        hasSavedValue(value) {
            return value !== "" && value !== null && value !== undefined;
        },

        hasSavedPicture(pictureType) {
            return (this.savedFreshWaterForm?.pictures || []).some(
                item => Number(item.pictureType) === Number(pictureType) && item.pictureUrl
            );
        },

        emitFreshWaterSignAvailability() {
            this.$nextTick(() => {
                this.$emit("fresh-water-sign-available", this.hasFreshWaterSignAction);
            });
        },
    }
};
</script>

<style scoped>
.fresh-water-container {
    padding: 12px 16px 24px;
    background: #fff;
}

.section {
    border-top: 1px solid #e5e9f2;
    padding: 18px 0 10px;
}

.section-title {
    font-size: 14px;
    font-weight: 700;
    color: #4a5568;
    margin-bottom: 16px;
    text-transform: uppercase;
}

.declaration-text p {
    margin: 0 0 14px;
    line-height: 1.7;
    color: #606266;
    font-size: 13px;
}

.signature-box-title {
    font-weight: 600;
    color: #7d8796;
    margin-bottom: 12px;
}

.signature-pad-box {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a0aec0;
    background: #fff;
}

.totaliser-image-row {
    color: #82889c;
}

.totaliser-image-row .el-button+.el-button {
    margin-left: 6px;
}

.saved-signature-image {
    height: 200px;
    width: 100%;
    max-width: 400px;
    object-fit: contain;
    border: 1px solid #ccd0de;
    border-radius: 10px;
}

.signature-draw-wrapper {
    position: relative;
}

.signature-clear {
    position: absolute;
    top: 5px;
    right: 5px;
    border-radius: 50%;
    z-index: 2;
    cursor: pointer;
}

.image-save-reminder {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    color: #3c58e6;
    font-size: 12px;
    position: relative;
    top: -12px;
}

.utms-order-no-label {
    color: #f56c6c;
    font-size: 12px;
    font-weight: 600;
    margin-left: 8px;
}

.signing-context-box {
    margin-bottom: 16px;
    padding: 12px 14px;
    border: 1px solid #3c64e6;
    border-radius: 4px;
    background: #e3e8ff;
    position: relative;
    top: -12px;
}

.signing-context-title {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
}

.signing-context-value {
    font-size: 15px;
    font-weight: 700;
    color: #3c4ae6;
}
</style>